import React, { useEffect, useState } from "react";
import {
    ButtonProps,
    DropdownProps,
    Form,
    InputOnChangeData,
    Label,
    Modal,
    Step,
    TextAreaProps
} from "semantic-ui-react";

import ModalApp from "../../components/ModalApp";
import { IGerenteRegional, IGerenteSetor, IRecebedor, IRepresentante, IRevendedor, Roles } from "../../interfaces";
import errors, { IAliancaErrors } from "../../Utils/ExtractErrors";

import NotificationRepository from "../../Repositories/NotificationRepository";
import RegionalManagerRepository from "../../Repositories/RegionalManagerRepository";
import SectorManagerRepository from "../../Repositories/SectorManagerRepository";
import AgentsRepository from "../../Repositories/AgentsRepository";
import OperatorRepository from "../../Repositories/OperatorRepository";
import { toast } from "react-toastify";

enum eNotificationStep {
    NivelEnvio = 0,
    Recebedores = 1,
    Mensagem = 2
}

enum eNotificationChoice {
    Todos = 0,
    GerenteRegional = 1,
    GerenteSetor = 2,
    Representante = 3,
    Operador = 4
}

interface IPersonChoice extends IGerenteRegional, IGerenteSetor, IRepresentante, IRevendedor {
    id: string | "*";
}

interface IProps {
    onSuccess: VoidFunction;
}

interface IState {
    open: boolean;
    step: eNotificationStep;
    choice: eNotificationChoice | undefined;
    loading: boolean;
    sending: boolean;
    description: string;
    message: string;
    apiErrors: IAliancaErrors;
    regionalManagers: Pick<IPersonChoice, "id" | "nome">[];
    regionalManagersChoices: string[];
    sectorManagers: Pick<IPersonChoice, "id" | "nome" | "gerenteRegionalId">[];
    sectorManagersChoices: string[];
    agents: Pick<IPersonChoice, "id" | "nome" | "gerenteSetorId">[];
    agentsChoices: string[];
    operators: Pick<IPersonChoice, "id" | "nome">[];
    operatorsChoices: string[];
}

const initialState: IState = {
    open: false,
    step: eNotificationStep.NivelEnvio,
    choice: undefined,
    loading: true,
    sending: false,
    description: "",
    message: "",
    apiErrors: { errors: [] },
    regionalManagers: [],
    regionalManagersChoices: [],
    sectorManagers: [],
    sectorManagersChoices: [],
    agents: [],
    agentsChoices: [],
    operators: [],
    operatorsChoices: []
}

export default function AdminCadastrarNotificacao(props: IProps) {
    const [state, setState] = useState<IState>(initialState);

    useEffect(() => {
        const error = errors.getErrors({ errors: state.apiErrors.errors }, "ApiError");
        if (error !== undefined) {
            toast(error, { position: "top-center", type: "error" });
        }
    }, [state.apiErrors]);

    async function load() {
        try {
            setState(state => ({ ...state, open: true, loading: true }));
            const [regional, sector, agent, operator] = await Promise.all([
                RegionalManagerRepository.List({
                    filter: "STATUS == 1 && DEVEDOR == 0"
                }),
                SectorManagerRepository.List({
                    filter: "STATUS == 1 && DEVEDOR == 0"
                }),
                AgentsRepository.List({
                    filter: "STATUS == 1 && DEVEDOR == 0"
                }),
                OperatorRepository.List({})
            ]);

            setState(state => ({
                ...state,
                regionalManagers: [
                    { id: "*", nome: "Todos" },
                    ...regional.list
                ] as IPersonChoice[],
                sectorManagers: [
                    { id: "*", nome: "Todos" },
                    ...sector.list
                ] as IPersonChoice[],
                agents: [
                    { id: "*", nome: "Todos" },
                    ...agent.list
                ] as IPersonChoice[],
                operators: [
                    { id: "*", nome: "Todos" },
                    ...operator.list
                ] as IPersonChoice[],
                loading: false
            }));
        } catch (err) {
            if (err.response.status === 401) {
                window.location.href = "/";
            }
            setState(state => ({ ...state, apiErrors: err.response.data, loading: false }));
        }
    }

    function getReceiversByUserLevel({ choices, persons, userLevel }: { choices: string[], persons: Pick<IPersonChoice, "id" | "nome">[], userLevel: Roles }): Pick<IRecebedor, "recebedorId" | "userLevel">[] {
        if (choices.length === 0) {
            return [];
        }
        if (choices.includes("*")) {
            return persons.map(person => ({ recebedorId: person.id, userLevel: userLevel }));
        }
        return choices.map(choice => ({ recebedorId: choice, userLevel: userLevel }));
    }

    function getReceivers() {
        let receivers: Pick<IRecebedor, "recebedorId" | "userLevel">[] = [];
        switch (state.choice) {
            case eNotificationChoice.Todos:
                state.regionalManagers.filter(person => person.id !== "*")
                    .forEach(person => receivers.push({ recebedorId: person.id, userLevel: "GerenteR" }));
                state.sectorManagers.filter(person => person.id !== "*")
                    .forEach(person => receivers.push({ recebedorId: person.id, userLevel: "GerenteS" }));
                state.agents.filter(person => person.id !== "*")
                    .forEach(person => receivers.push({ recebedorId: person.id, userLevel: "Representante" }));
                break;
            case eNotificationChoice.GerenteRegional:
                receivers = getReceiversByUserLevel({
                    choices: state.regionalManagersChoices,
                    persons: state.regionalManagers,
                    userLevel: "GerenteR"
                });
                break;
            case eNotificationChoice.GerenteSetor:
                receivers = getReceiversByUserLevel({
                    choices: state.sectorManagersChoices,
                    persons: state.sectorManagers,
                    userLevel: "GerenteS"
                });
                break;
            case eNotificationChoice.Representante:
                receivers = getReceiversByUserLevel({
                    choices: state.agentsChoices,
                    persons: state.agents,
                    userLevel: "Representante"
                });
                break;
            case eNotificationChoice.Operador:
                receivers = getReceiversByUserLevel({
                    choices: state.operatorsChoices,
                    persons: state.operators,
                    userLevel: "Operador"
                });
                break;
        }
        return receivers;
    }

    async function handleSubmit() {
        try {
            setState(state => ({ ...state, sending: true }));
            const receivers = getReceivers();
            const response = await NotificationRepository.Create({
                descricao: state.description,
                mensagem: state.message,
                recebedores: receivers
            });
            setState(initialState);
            props.onSuccess();
            toast(response.message, { position: "top-center", type: "success" });
        } catch (err) {
            if (err.response.status === 401) {
                window.location.href = "/";
            }
            setState(state => ({ ...state, apiErrors: err.response.data, sending: false }));
        }
    }

    function hasError(key: string) {
        return errors.hasError(state.apiErrors, key);
    }

    function getMessage(key: string) {
        return errors.getMessage(state.apiErrors, key);
    }

    function handleOnOpen() {
        load();
    }

    function handleOnClose() {
        setState(initialState);
    }

    function handleOnChangeInput(event: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) {
        setState(state => ({ ...state, [data.name]: data.value }));
    }

    function handleOnChangeTextArea(event: React.FormEvent<HTMLTextAreaElement>, data: TextAreaProps) {
        setState(state => ({ ...state, [data.name]: data.value }));
    }

    function handleOnChangeDropdown(event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) {
        setState(state => ({ ...state, [data.name]: data.value }));
    }

    function handleStep(step: eNotificationStep) {
        switch (step) {
            case eNotificationStep.Recebedores:
                setState(state => ({ ...state, step: step }));
                break;
            case eNotificationStep.Mensagem:
                setState(state => ({ ...state, step: step }));
                break;
            default:
                setState(state => ({ ...state, step: eNotificationStep.NivelEnvio, choice: undefined }));
                break;
        }
    }

    function handleChoose(choice: eNotificationChoice) {
        if (choice === eNotificationChoice.Todos) {
            setState(state => ({ ...state, step: eNotificationStep.Mensagem, choice: choice }));
        } else {
            setState(state => ({ ...state, step: eNotificationStep.Recebedores, choice: choice }));
        }
    }

    const steps = [
        {
            key: "sendlevel",
            active: state.step === eNotificationStep.NivelEnvio,
            icon: "question circle",
            title: "Nível de envio",
            description: "Escolha o nível de envio",
            onClick: () => handleStep(eNotificationStep.NivelEnvio)
        },
        {
            key: "receivers",
            active: state.step === eNotificationStep.Recebedores,
            disabled: state.choice === undefined || state.choice === eNotificationChoice.Todos,
            icon: "user",
            title: "Recebedores",
            description: "Escolha os recebedores",
            onClick: () => handleStep(eNotificationStep.Recebedores)
        },
        {
            key: "message",
            active: state.step === eNotificationStep.Mensagem,
            disabled: state.choice === undefined || state.regionalManagersChoices.length === 0 || state.sectorManagersChoices.length === 0 || state.agentsChoices.length === 0,
            icon: "mail",
            title: "Mensagem",
            description: "Escreva a mensagem",
            onClick: () => handleStep(eNotificationStep.Mensagem)
        },
    ];

    const stepsGroup = (
        <Step.Group fluid items={steps} />
    );

    const sendLevelChoices: ButtonProps[] = [
        {
            key: "todos",
            content: "Todos",
            color: "blue",
            onClick: () => handleChoose(eNotificationChoice.Todos)
        },
        {
            key: "gerenteregional",
            content: "Gerente regional",
            color: "purple",
            onClick: () => handleChoose(eNotificationChoice.GerenteRegional)
        },
        {
            key: "gerentesetor",
            content: "Gerente de setor",
            color: "pink",
            onClick: () => handleChoose(eNotificationChoice.GerenteSetor)
        },
        {
            key: "representante",
            content: "Representante",
            color: "violet",
            onClick: () => handleChoose(eNotificationChoice.Representante)
        },
        {
            key: "operador",
            content: "Operador",
            color: "teal",
            onClick: () => handleChoose(eNotificationChoice.Operador)
        }
    ];

    const sendLevelContent = (
        <Modal.Content>
            {stepsGroup}
            <Form>
                {sendLevelChoices.map(choice => (
                    <Form.Button
                        fluid
                        key={choice.key}
                        content={choice.content}
                        color={choice.color}
                        onClick={choice.onClick}
                    />
                ))}
            </Form>
        </Modal.Content>
    )

    const choicesContent = (
        <Modal.Content>
            {stepsGroup}
            <Form>
                {((state.choice || 0) !== eNotificationChoice.Operador) &&
                    <Form.Field>
                        <label>Gerente regional</label>
                        <Form.Dropdown
                            selection
                            search
                            multiple
                            placeholder="Selecione"
                            noResultsMessage="Nenhum registro encontrado."
                            name="regionalManagersChoices"
                            options={state.regionalManagers
                                .filter(field => state.choice === eNotificationChoice.GerenteRegional ? true : field.id !== "*")
                                .filter(field => state.regionalManagersChoices.includes("*") ? field.id === "*" : true)
                                .map(field => { return { key: field.id, text: field.nome, value: field.id } })}
                            value={state.regionalManagersChoices}
                            onChange={handleOnChangeDropdown}
                        />
                    </Form.Field>
                }
                {((state.choice || 0) >= eNotificationChoice.GerenteSetor && (state.choice || 0) !== eNotificationChoice.Operador) &&
                    <Form.Field>
                        <label>Gerente de setor</label>
                        <Form.Dropdown
                            selection
                            search
                            multiple
                            placeholder="Selecione"
                            noResultsMessage="Nenhum registro encontrado."
                            disabled={state.regionalManagersChoices.length === 0}
                            name="sectorManagersChoices"
                            options={state.sectorManagers
                                .filter(field => state.choice === eNotificationChoice.GerenteSetor ? true : field.id !== "*")
                                .filter(field => state.regionalManagersChoices.includes(field.gerenteRegionalId))
                                .filter(field => state.sectorManagersChoices.includes("*") ? field.id === "*" : true)
                                .map(field => { return { key: field.id, text: field.nome, value: field.id } })}
                            value={state.sectorManagersChoices}
                            onChange={handleOnChangeDropdown}
                        />
                    </Form.Field>
                }
                {((state.choice || 0) >= eNotificationChoice.Representante && (state.choice || 0) !== eNotificationChoice.Operador) &&
                    <Form.Field>
                        <label>Representante</label>
                        <Form.Dropdown
                            selection
                            search
                            multiple
                            placeholder="Selecione"
                            noResultsMessage="Nenhum registro encontrado."
                            disabled={state.sectorManagersChoices.length === 0}
                            name="agentsChoices"
                            options={state.agents
                                .filter(field => state.choice === eNotificationChoice.Representante ? true : field.id !== "*")
                                .filter(field => state.sectorManagersChoices.includes(field.gerenteSetorId))
                                .filter(field => state.agentsChoices.includes("*") ? field.id === "*" : true)
                                .map(field => { return { key: field.id, text: field.nome, value: field.id } })}
                            value={state.agentsChoices}
                            onChange={handleOnChangeDropdown}
                        />
                    </Form.Field>
                }
                {((state.choice || 0) === eNotificationChoice.Operador) &&
                    <Form.Field>
                        <label>Operador</label>
                        <Form.Dropdown
                            selection
                            search
                            multiple
                            placeholder="Selecione"
                            noResultsMessage="Nenhum registro encontrado."
                            name="operatorsChoices"
                            options={state.operators
                                .filter(field => state.operatorsChoices.includes("*") ? field.id === "*" : true)
                                .map(field => { return { key: field.id, text: field.nome, value: field.id } })}
                            value={state.operatorsChoices}
                            onChange={handleOnChangeDropdown}
                        />
                    </Form.Field>
                }
                <Form.Button
                    fluid
                    content="Escrever notificação"
                    color="pink"
                    onClick={() => handleStep(eNotificationStep.Mensagem)}
                />
            </Form>
        </Modal.Content>
    );

    const messageContent = (
        <Modal.Content>
            {stepsGroup}
            <Form>
                <Form.Field error={hasError("Descricao")} required>
                    <label>Descrição</label>
                    <Form.Input
                        placeholder="Descrição"
                        name="description"
                        value={state.description}
                        onChange={handleOnChangeInput}
                    />
                    {hasError("Descricao") && <Label pointing prompt>{getMessage("Descricao")}</Label>}
                </Form.Field>
                <Form.Field error={hasError("Mensagem")} required>
                    <label>Mensagem</label>
                    <Form.TextArea
                        placeholder="Mensagem"
                        name="message"
                        value={state.message}
                        onChange={handleOnChangeTextArea}
                    />
                    {hasError("Mensagem") && <Label pointing prompt>{getMessage("Mensagem")}</Label>}
                </Form.Field>
            </Form>
        </Modal.Content>
    );

    return (
        <ModalApp
            title="Nova"
            loading={state.loading}
            open={state.open}
            handleOpen={handleOnOpen}
            handleClose={handleOnClose}
            trigger={{
                icon: "add",
                color: "pink",
                content: "Nova",
                labelPosition: "left",
                floated: "right"
            }}
            content={
                <>
                    {state.step === 0 && sendLevelContent}
                    {state.step === 1 && choicesContent}
                    {state.step === 2 && messageContent}
                </>
            }
            onSuccess={{
                content: "Enviar",
                func: handleSubmit,
                disabled: state.step !== eNotificationStep.Mensagem,
                loading: state.sending
            }}
        />
    )
}
